'use client';

import { BackArrow } from '@ahamove/icons';
import { shadow } from '@ahamove/tokens';
import { FloatingLabelInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  Radio,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type ItemTypeModalProps = ContextModalProps<{
  initialValue?: string;
  onConfirm?: (newValue: string) => void;
}>;

export default function ItemTypeModal({
  context,
  id,
  innerProps,
}: ItemTypeModalProps) {
  const tActions = useTranslations('common.actions');
  const tPackageDetails = useTranslations('booking.package_details');

  const isMobile = useIsMobile();

  const { pathInfo, setPathInfoByIndex } = useBookingStore((state) => state);

  const nationWidePathInfo = useMemo(() => {
    return pathInfo?.[1];
  }, [pathInfo]);

  const itemTypes = [
    { value: 'cosmetic', label: tPackageDetails('item_types.cosmetic') },

    { value: 'pharma', label: tPackageDetails('item_types.pharma') },
    { value: 'food', label: tPackageDetails('item_types.food') },
    { value: 'beverage', label: tPackageDetails('item_types.beverage') },
    { value: 'material', label: tPackageDetails('item_types.material') },
    {
      value: 'machine_equipment_electricity',
      label: tPackageDetails('item_types.machine_equipment_electricity'),
    },
    {
      value: 'furniture_decoration',
      label: tPackageDetails('item_types.furniture_decoration'),
    },
    {
      value: 'packaging_garment',
      label: tPackageDetails('item_types.packaging_garment'),
    },
    { value: 'other', label: tPackageDetails('item_types.other') },
  ];

  const schema = z
    .object({
      choice: z.string().nonempty('Please select an option'),
      other_text: z.string().optional(),
    })
    .refine(
      (val) => {
        if (val.choice === 'other' && !val.other_text?.trim()) {
          return false;
        }
        return true;
      },
      {
        message: 'Please provide details for "Other" option',
        path: ['other_text'],
      }
    );

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      choice: '',
      other_text: '',
    },
  });
  useEffect(() => {
    if (!innerProps.initialValue) return;

    const matchedItem = itemTypes.find(
      (item) => item.label === innerProps.initialValue
    );
    if (matchedItem) {
      setValue('choice', matchedItem.value);
    } else {
      setValue('choice', 'other');
      setValue('other_text', innerProps.initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerProps.initialValue, setValue]);

  const watchChoice = watch('choice');

  const onSubmit = (data: FormData) => {
    const selectedOption = itemTypes.find((item) => item.value === data.choice);
    const itemDescription =
      data.choice === 'other' ? data.other_text : selectedOption?.label;

    if (itemDescription) {
      setPathInfoByIndex(
        1,
        nationWidePathInfo
          ? { ...nationWidePathInfo, item_description: itemDescription }
          : { item_description: itemDescription }
      );
      innerProps.onConfirm?.(itemDescription);
    }

    handleCloseModal();
  };

  const handleCloseModal = () => {
    context.closeModal(id);
  };

  return (
    <>
      <Group
        gap="xl"
        align="center"
        justify="space-between"
        w="100%"
        px={{ base: 'md', sm: rem(24) }}
        py={{ base: rem(10), sm: 'md' }}
        pos="sticky"
        top={0}
        left={0}
        bg="white"
        styles={{
          root: {
            zIndex: 100,
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        <ActionIcon
          size={rem(24)}
          variant="transparent"
          onClick={handleCloseModal}
        >
          <BackArrow width={24} height={24} />
        </ActionIcon>
        <Title
          order={2}
          size={isMobile ? 'h4' : 'h3'}
          flex={1}
          ta={isMobile ? 'center' : 'left'}
        >
          {tPackageDetails('select_item_type')}
        </Title>
        <Space w={rem(24)} />
      </Group>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="choice"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio.Group
              mt="md"
              mb={{ base: rem(96), sm: 'md' }}
              bg="white"
              value={value}
              onChange={(value) => onChange(value)}
            >
              <Stack gap={0}>
                {itemTypes.map((item, idx) => {
                  return (
                    <Fragment key={item.value}>
                      {idx > 0 ? <Divider mx="md" /> : null}
                      <Button
                        variant="subtle"
                        size="md"
                        p="md"
                        h="auto"
                        color="neutral"
                        fw={500}
                        fullWidth
                        radius={0}
                        justify="space-between"
                        component="label"
                        htmlFor={`item-type-${item.value}`}
                        rightSection={
                          <Radio
                            value={item.value}
                            id={`item-type-${item.value}`}
                            checked={value === item.value}
                          />
                        }
                        styles={{
                          root: {},
                        }}
                      >
                        {item.label}
                      </Button>
                    </Fragment>
                  );
                })}
                <Collapse in={watchChoice === 'other'}>
                  <Box pt="sm" px="md" pb="md">
                    <Controller
                      control={control}
                      name="other_text"
                      render={({ field: { onChange, value } }) => (
                        <FloatingLabelInput
                          error={errors.other_text?.message}
                          label={tPackageDetails('enter_the_item_type')}
                          value={value}
                          onChange={(value) => onChange(value)}
                        />
                      )}
                    />
                  </Box>
                </Collapse>
                {errors.choice?.message ? (
                  <Text size="sm" pt={rem(4)} px="md" pb="sm" c="red.6">
                    {errors.choice?.message}
                  </Text>
                ) : null}
              </Stack>
            </Radio.Group>
          )}
        />

        <Group
          justify="flex-end"
          mt="auto"
          gap={8}
          p="md"
          pos={{ base: 'fixed', sm: 'sticky' }}
          bottom={0}
          left={0}
          w="100%"
          bg="white"
          styles={{
            root: {
              zIndex: 100,
              borderTop: isMobile
                ? 'none'
                : `1px solid var(--mantine-color-neutral-1)`,
              boxShadow: isMobile ? shadow.buttonDock : 'none',
            },
          }}
        >
          <Button
            fullWidth
            size="md"
            mih={rem(48)}
            type="submit"
            disabled={!isValid}
          >
            {tActions('confirm')}
          </Button>
        </Group>
      </form>
    </>
  );
}
